// Right column — kill/death tally + 4 vertical stat bars (shells, mines,
// armour, trees) + legend icons row at the bottom of the bars panel.
//
// Bar order matches Cheshire's canonical layout from `Brain.h` (BrainInfo
// struct field order: shells, mines, armour, trees) and the original Mac
// Bolo's status panel labelling — see PICT 128 in `Bolo 0.99.7` for the
// icon-to-bar mapping.
//
// Legend icons (shells / mines / armour / trees) are CSS background-image
// inline SVGs traced pixel-perfect from PICT 128; see the .legend-icon-*
// rules in index.html.

import {
  TANK_FULL_ARMOUR,
  TANK_FULL_MINES,
  TANK_FULL_SHELLS,
  TANK_FULL_TREES,
} from '@bolo2k/shared';
import type { ClientState } from '@bolo2k/client-core';

interface BarRefs {
  fill: HTMLElement;
  max: number;
}

export class RightColumn {
  private skullCount: HTMLElement;
  private tombCount: HTMLElement;
  private shells: BarRefs;
  private mines: BarRefs;
  private armour: BarRefs;
  private trees: BarRefs;
  // Snapshot-version gate — tank stats (kills/deaths/shells/mines/armour/
  // trees) come from the server and never change between snapshots, so
  // there's no point repainting at 60 Hz.
  private lastVersion = -1;

  constructor(tallyHost: HTMLElement, barsHost: HTMLElement) {
    // ---------- Tally ----------
    tallyHost.classList.add('tally-body');
    const skullRow = document.createElement('div');
    skullRow.className = 'tally-row';
    const skullIcon = document.createElement('span');
    skullIcon.className = 'tally-icon tally-icon-kills';
    this.skullCount = document.createElement('span');
    this.skullCount.className = 'tally-num';
    this.skullCount.textContent = '0';
    skullRow.append(skullIcon, this.skullCount);

    const tombRow = document.createElement('div');
    tombRow.className = 'tally-row';
    const tombIcon = document.createElement('span');
    tombIcon.className = 'tally-icon tally-icon-deaths';
    this.tombCount = document.createElement('span');
    this.tombCount.className = 'tally-num';
    this.tombCount.textContent = '0';
    tombRow.append(tombIcon, this.tombCount);

    tallyHost.append(skullRow, tombRow);

    // ---------- Bars + legend ----------
    // Bar order is shells / mines / armour / trees per Cheshire's Brain.h.
    barsHost.classList.add('bars-body');
    const barsRow = document.createElement('div');
    barsRow.className = 'bars-row';
    this.shells = makeBar(barsRow, TANK_FULL_SHELLS);
    this.mines = makeBar(barsRow, TANK_FULL_MINES);
    this.armour = makeBar(barsRow, TANK_FULL_ARMOUR);
    this.trees = makeBar(barsRow, TANK_FULL_TREES);

    const legend = document.createElement('div');
    legend.className = 'bars-legend';
    legend.append(
      iconCell('shells'),
      iconCell('mines'),
      iconCell('armour'),
      iconCell('trees'),
    );
    barsHost.append(barsRow, legend);
  }

  draw(state: ClientState): void {
    if (state.version === this.lastVersion) return;
    const me = state.tanks.find((t) => t.id === state.myTankId);
    if (!me) return;
    setText(this.skullCount, String(me.kills));
    setText(this.tombCount, String(me.deaths));
    setBar(this.shells, me.shells);
    setBar(this.mines, me.mines);
    setBar(this.armour, me.armour);
    setBar(this.trees, me.trees);
    this.lastVersion = state.version;
  }
}

function makeBar(parent: HTMLElement, max: number): BarRefs {
  const cell = document.createElement('div');
  cell.className = 'bar-cell';
  const fill = document.createElement('div');
  fill.className = 'bar-fill';
  cell.appendChild(fill);
  parent.appendChild(cell);
  return { fill, max };
}

function iconCell(kind: 'shells' | 'mines' | 'armour' | 'trees'): HTMLElement {
  const cell = document.createElement('span');
  cell.className = `legend-icon legend-icon-${kind}`;
  return cell;
}

function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

function setBar(b: BarRefs, value: number): void {
  const pct = Math.max(0, Math.min(1, value / b.max)) * 100;
  const h = `${pct.toFixed(1)}%`;
  if (b.fill.style.height !== h) b.fill.style.height = h;
}
